import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Table from "src/componets/Table";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { InvoiceInterface } from "src/store/callrecorder/invoicesSlice";
import { fetchInvoices } from "src/actions/callrecorder/invoicesActions";
import { queryParams } from "src/helpers/UrlHelpers";
import { formattedDate } from "src/helpers/DateHelpers";
import Pagination from "src/componets/Pagination";
import OptionalLink from "src/componets/OptionalLink";
import callrecorderUrls from "src/configs/callrecorder/callrecorderUrls";

import { Tag } from "antd";

interface ListTableInterface {
  filterParams: object;
  limit?: number;
}

const _ListTable = ({ filterParams, limit = 25 }: ListTableInterface) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const currentPage = queryParams.getInt(searchParams, 'page') || 1;
  const ids = useAppSelector(state => state.callrecorder.invoices.ids);
  const entities = useAppSelector(state => state.callrecorder.invoices.entities);
  const count = useAppSelector(state => state.callrecorder.invoices.count);
  const data = ids ? ids.map(id => entities[id]) : [];

  const params = useMemo(() => ({
    limit,
    offset: (currentPage - 1) * limit,
    ...filterParams
  }), [limit, currentPage, filterParams]);

  useEffect(() => {
    dispatch(fetchInvoices(params));
  }, [params, dispatch]);

  const typeColor = (typeName: string | null) => {
    switch (typeName) {
      case 'buy_provider_number': return 'blue';
      case 'phone_number_renewal': return 'green';
      case 'incoming_sms_overlimit': return 'warning';
      case 'buy_extra_sms': return 'purple';
      default: return 'default';
    }
  };

  const columns: ColumnsType<InvoiceInterface> = [
    {
      key: 'id',
      title: 'ID',
      render: (_, { id }) => <small>{`#${id}`}</small>,
      width: 60
    },

    {
      key: 'type_name',
      title: 'Type',
      render: (_, { type_name }) => type_name ? <Tag color={typeColor(type_name)}>{type_name}</Tag> : '—',
      width: 160
    },

    {
      key: 'amount',
      title: 'Amount',
      render: (_, { amount }) => amount ? <small>{amount}</small> : '—',
      width: 80
    },

    {
      key: 'consume',
      title: 'Consume',
      render: (_, { consume }) => <Tag color={consume ? 'success' : 'default'}>{consume ? 'Yes' : 'No'}</Tag>,
      width: 80
    },

    {
      key: 'user_id',
      title: 'User',
      render: (_, { user_id }) => user_id ? (
        <OptionalLink link={callrecorderUrls.userPath(user_id)}>
          <small>{`#${user_id}`}</small>
        </OptionalLink>
      ) : '—',
      width: 60
    },

    {
      key: 'user_number_id',
      title: 'User Number',
      render: (_, { user_number_id }) => user_number_id ? (
        <OptionalLink link={callrecorderUrls.userNumberPath(user_number_id)}>
          <small>{`#${user_number_id}`}</small>
        </OptionalLink>
      ) : '—',
      width: 90
    },

    {
      key: 'created_at',
      title: 'Created',
      render: (_, { created_at }) => <small>{formattedDate(created_at).dateTime}</small>,
      width: 125
    },
  ];

  return (
    <div>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
